import { Box, useTheme } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import type { ReactNode } from 'react';
import { useMemo } from 'react';

import type {
    DesignSystemCardDirection,
    DesignSystemCardSize,
} from '../../theme/design-system/types';
import type { Slots } from '../../utils/Slots';
import { SlotProvider } from '../../utils/SlotContext';
import { CardTitle } from './CardTitle';
import { CardDescription } from './CardDescription';
import { CardActions } from './CardActions';
import { CardContent } from './CardContent';
import { DEFAULT_CARD_DIRECTION, DEFAULT_CARD_SIZE } from './Card.constants';
import {
    sizeTokens,
    leadItemSizeMap,
    getLayoutStyles,
    GRID_AREA_LEAD,
} from './Card.styleUtils';

export type { DesignSystemCardSize as CardSize } from '../../theme/design-system/types';
export type { DesignSystemCardDirection as CardDirection } from '../../theme/design-system/types';

export type CardProps = {
    direction?: DesignSystemCardDirection;
    size?: DesignSystemCardSize;
    children: ReactNode;
    sx?: SxProps<Theme>;
};

const CardBase = ({
    children,
    direction = DEFAULT_CARD_DIRECTION,
    size = DEFAULT_CARD_SIZE,
    sx,
}: CardProps) => {
    const theme = useTheme();

    const cardStyles: SxProps<Theme> = useMemo(() => {
        const tokens = sizeTokens[size];

        return {
            ...getLayoutStyles(direction, tokens),
            padding: tokens.padding,
            borderRadius:
                theme.borders.ds.borderRadius[tokens.borderRadiusToken],
            border: `1px solid ${theme.palette.ds.border.neutral.primary}`,
            backgroundColor: theme.palette.ds.bg.neutral.primary,
            ...sx,
        };
    }, [direction, size, sx, theme]);

    const isVerticalActions = direction === 'vertical-actions';

    const slots: Slots = useMemo(
        () => ({
            card: { size, direction },
            leadItem: {
                size: leadItemSizeMap[size],
                ...(isVerticalActions && {
                    sx: {
                        gridArea: GRID_AREA_LEAD,
                        alignSelf: 'start',
                        marginRight: sizeTokens[size].gap.default,
                    },
                }),
            },
        }),
        [size, direction, isVerticalActions],
    );

    return (
        <SlotProvider value={slots}>
            <Box sx={cardStyles}>{children}</Box>
        </SlotProvider>
    );
};

export const Card = Object.assign(CardBase, {
    Title: CardTitle,
    Description: CardDescription,
    Content: CardContent,
    Actions: CardActions,
});
